import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ActionMenuItem } from '@seahorse/ui';
import {
  ShButtonComponent,
  ShMenuButtonComponent,
  ShMenuComponent,
  ShMenuItem,
  ShSkeletonComponent,
  ShStatusComponent,
} from '@seahorse/components';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { BulkBarComponent } from '../bulk-bar/bulk-bar.component';
import { CellCodeComponent } from '../cell-code/cell-code.component';
import { CellProgressComponent } from '../cell-progress/cell-progress.component';
import { CellTwoLineComponent } from '../cell-two-line/cell-two-line.component';
import { RowOpenDirective } from '../row-open/row-open.directive';
import {
  SH_TABLE_DEFAULT_DENSITY,
  SH_TABLE_DENSITIES,
  SH_TABLE_PREFERENCES,
  ShTableDensity,
} from '../table-preferences';
import type {
  RowOpenMode,
  ShColumn,
  ShColumnType,
  ShTablePaging,
  ShTableQuery,
  ShTableSort,
} from '../table.models';
import { ShCellValuePipe } from './cell-value.pipe';
import { ShCellDirective, ShEmptyDirective } from './table.directives';

interface SkeletonBar {
  width: string;
  height: string;
}

/** What the template needs per column; built once per `columns` value. */
interface ColumnView {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: ShColumn<any>;
  type: ShColumnType;
  /** The sort field name, when the column is sortable. */
  sortField: string | undefined;
  cellClass: string;
  width: string | null;
  truncate: boolean;
  format: string | undefined;
  skeleton: SkeletonBar[];
}

/** The header row shown above the first row of a group. */
interface GroupHeader {
  key: string;
  label: string;
  count: number;
}

const EMPTY_ROWS: unknown[] = [];
const EMPTY_HEADERS = new Map<unknown, GroupHeader>();
const NO_ACTIONS: ActionMenuItem<never>[] = [];

/** `row.a.b` for the path `'a.b'`, like PrimeNG's `dataKey`. */
function resolvePath(row: unknown, path: string): unknown {
  let value: unknown = row;
  for (const part of path.split('.')) {
    if (value === null || value === undefined) return undefined;
    value = (value as Record<string, unknown>)[part];
  }
  return value;
}

function sameSort(a: ShTableSort | undefined, b: ShTableSort | undefined) {
  return a?.field === b?.field && a?.order === b?.order;
}

function skeletonFor(type: ShColumnType): SkeletonBar[] {
  switch (type) {
    case 'code':
      return [{ width: '80px', height: '1rem' }];
    case 'status':
      return [{ width: '90px', height: '1.5rem' }];
    case 'twoLine':
      return [
        { width: '140px', height: '1rem' },
        { width: '90px', height: '0.75rem' },
      ];
    case 'date':
    case 'dateTime':
      return [{ width: '110px', height: '1rem' }];
    case 'number':
      return [{ width: '60px', height: '1rem' }];
    case 'progress':
      return [{ width: '100px', height: '0.5rem' }];
    case 'boolean':
      return [{ width: '40px', height: '1rem' }];
    default:
      return [{ width: '140px', height: '1rem' }];
  }
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined;
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function sameNumbers(a: number[] | undefined, b: number[] | undefined) {
  return (
    a === b ||
    (!!a && !!b && a.length === b.length && a.every((v, i) => v === b[i]))
  );
}

/**
 * A config-driven table over PrimeNG's `p-table`: pages describe the columns
 * (`ShColumn`), the table renders the cells, skeleton rows, empty state,
 * sorting, paging and the density control.
 *
 *   <sh-table
 *     tableId="invoices"
 *     [columns]="columns"
 *     [rows]="rows"
 *     [paging]="{ mode: 'client', pageSize: 10 }"
 *     [loading]="loading" />
 *
 * One cell can be replaced: `<ng-template shCell="key" let-row let-value="value">`;
 * the empty state with `<ng-template shEmpty>`.
 *
 * Paging and sorting: `paging` unset renders all rows, no paginator. `client`
 * mode pages and sorts `rows` itself. `server` mode shows `rows` as is (they
 * are the current page) and the page reloads on `queryChange`; mark only the
 * columns the API can sort as `sortable`. `queryChange` emits in both modes.
 *
 * Density (comfortable / compact / condensed) is a per-user, per-table choice
 * kept behind `SH_TABLE_PREFERENCES`; the `density` input forces one. Without
 * either, a table is `defaultDensity`, or condensed (`SH_TABLE_DEFAULT_DENSITY`,
 * decided 2026-10-05: lists read better dense).
 *
 * Features that switch on by input:
 * - `selectable` + `[(selection)]`: a checkbox column (the header checkbox
 *   selects the current page); the selection holds the row objects as they
 *   were selected and survives page changes (identity is `dataKey`). With
 *   `bulkActions` a `sh-bulk-bar` shows while rows are selected.
 * - `rowClass`: extra classes per row (row tones).
 * - `rowActions`: a last, header-less column with a `sh-menu-button` (the row
 *   is the menu context).
 * - `rowOpenMode` + `activeRow` + `(rowOpen)`: `shRowOpen` on the rows;
 *   `activeRow` is a `dataKey` value.
 * - `groupBy` (+ `groupLabel`): a group header row above each group. Client
 *   mode orders the rows by group (groups in order of first appearance), then
 *   by the active sort; server mode shows the rows as given, so the API must
 *   return them grouped. Pass a stable function (a field, not a template
 *   literal).
 * - `urlParam="ex"`: page and sort live in `?ex-page=3&ex-sort=amount.desc`
 *   (page 1-based; no param at page 1 / no sort). Read on init and on every
 *   query-param change; written (`replaceUrl`, other params kept) when the
 *   user pages or sorts, like `sh-tabs`. Invalid values are ignored. Without
 *   a param in the URL the table is at page 1 with the sort it had on init.
 */
@Component({
  selector: 'sh-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'sh-table' },
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ShButtonComponent,
    ShMenuComponent,
    ShSkeletonComponent,
    ShStatusComponent,
    ShMenuButtonComponent,
    BulkBarComponent,
    RowOpenDirective,
    CellCodeComponent,
    CellTwoLineComponent,
    CellProgressComponent,
    ShCellValuePipe,
  ],
  template: `
    <div
      class="table-card"
      [class.table-density-compact]="effectiveDensity() === 'compact'"
      [class.table-density-condensed]="effectiveDensity() === 'condensed'"
    >
      <p-table
        [value]="shownRows()"
        [dataKey]="dataKey()"
        [selection]="selection()"
        [selectionPageOnly]="true"
        (selectionChange)="selection.set($event)"
        [lazy]="true"
        [lazyLoadOnInit]="false"
        [paginator]="!!paging()"
        [rows]="pageSize()"
        [first]="first()"
        [totalRecords]="totalRecords()"
        [rowsPerPageOptions]="pageSizeOptions()"
        [sortField]="sortField()"
        [sortOrder]="sortOrder()"
        [loading]="loading()"
        [showLoader]="false"
        [resetPageOnSort]="false"
        (onLazyLoad)="onLazyLoad($event)"
      >
        <ng-template pTemplate="header">
          <tr>
            @if (selectable()) {
            <th class="col-check">
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </th>
            } @for (view of columnViews(); track view.column.key) { @if
            (view.sortField) {
            <th
              [pSortableColumn]="view.sortField"
              [class]="view.cellClass"
              [style.width]="view.width"
            >
              {{ view.column.header | translate }}
              <p-sortIcon [field]="view.sortField"></p-sortIcon>
            </th>
            } @else {
            <th [class]="view.cellClass" [style.width]="view.width">
              {{ view.column.header | translate }}
            </th>
            } } @if (hasRowActions()) {
            <th class="col-actions"></th>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-row>
          @if (groupHeaders().get(row); as header) {
          <tr class="row-group-header">
            <td [attr.colspan]="columnCount()">
              <span class="group-title">{{ header.label }}</span>
              <span class="meta">{{
                'shell.table.groupCount' | translate: { count: header.count }
              }}</span>
            </td>
          </tr>
          }
          <tr
            [shRowOpen]="row"
            [shRowOpenMode]="rowOpenMode()"
            [shRowOpenActive]="isActive(row)"
            (rowOpen)="rowOpen.emit($event)"
            [class.p-datatable-row-selected]="isSelected(row)"
            [class]="rowClassOf(row)"
          >
            @if (selectable()) {
            <td class="col-check">
              <p-tableCheckbox [value]="row"></p-tableCheckbox>
            </td>
            } @for (view of columnViews(); track view.column.key) {
            <td
              [class]="view.cellClass"
              [attr.title]="
                view.truncate ? (row | shCellValue: view.column) : null
              "
            >
              @if (cellTemplates()[view.column.key]; as template) {
              <ng-container
                *ngTemplateOutlet="
                  template;
                  context: {
                    $implicit: row,
                    value: row | shCellValue: view.column
                  }
                "
              ></ng-container>
              } @else { @switch (view.type) { @case ('code') {
              <sh-cell-code
                [value]="row | shCellValue: view.column"
                [copy]="!!view.column.copy"
              ></sh-cell-code>
              } @case ('twoLine') {
              <sh-cell-two-line
                [primary]="row | shCellValue: view.column"
                [secondary]="view.column.secondary?.(row) ?? undefined"
                [plain]="!!view.column.plainPrimary"
              ></sh-cell-two-line>
              } @case ('status') { @if (view.column.statusMap; as statusMap) {
              <sh-status
                [status]="row | shCellValue: view.column"
                [mapping]="statusMap"
              ></sh-status>
              } } @case ('progress') { @if (view.column.progress) {
              <sh-cell-progress
                [done]="view.column.progress(row).done"
                [total]="view.column.progress(row).total"
              ></sh-cell-progress>
              } } @case ('date') {
              {{ row | shCellValue: view.column | date: view.format }}
              } @case ('dateTime') {
              {{ row | shCellValue: view.column | date: view.format }}
              } @case ('number') {
              {{ row | shCellValue: view.column | number: view.format }}
              } @case ('boolean') {
              {{
                (row | shCellValue: view.column)
                  ? ('shared.terms.yes' | translate)
                  : ('shared.terms.no' | translate)
              }}
              } @default {
              {{ row | shCellValue: view.column }}
              } } }
            </td>
            } @if (hasRowActions()) {
            <td class="col-actions">
              <sh-menu-button
                [items]="rowActions() ?? noActions"
                [context]="row"
              ></sh-menu-button>
            </td>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="loadingbody">
          @for (index of skeletonIndexes(); track index) {
          <tr>
            @if (selectable()) {
            <td class="col-check"></td>
            } @for (view of columnViews(); track view.column.key) {
            <td [class]="view.cellClass">
              @for (bar of view.skeleton; track $index) {
              <sh-skeleton
                [width]="bar.width"
                [height]="bar.height"
              ></sh-skeleton>
              }
            </td>
            } @if (hasRowActions()) {
            <td class="col-actions"></td>
            }
          </tr>
          }
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="columnCount()">
              <ng-container
                *ngTemplateOutlet="emptyTemplate() ?? defaultEmpty"
              ></ng-container>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="paginatorleft">
          <ng-container *ngTemplateOutlet="densityControl"></ng-container>
        </ng-template>
      </p-table>

      @if (bulkActions(); as actions) { @if (selection().length) {
      <sh-bulk-bar
        [count]="selection().length"
        [actions]="actions"
        [context]="selection()"
        (clear)="selection.set([])"
      ></sh-bulk-bar>
      } } @if (!paging() && showDensityControl()) {
      <div class="table-footer">
        <ng-container *ngTemplateOutlet="densityControl"></ng-container>
      </div>
      }
    </div>

    <ng-template #defaultEmpty>
      <div class="table-empty">
        {{ 'shell.table.empty' | translate }}
        @if (showClearFilters()) {
        <button
          type="button"
          class="table-empty-clear"
          (click)="clearFilters.emit()"
        >
          {{ 'shell.pageHeader.clearAll' | translate }}
        </button>
        }
      </div>
    </ng-template>

    <ng-template #densityControl>
      @if (showDensityControl()) {
      <sh-button
        variant="text"
        size="sm"
        [rounded]="true"
        icon="fa-solid fa-grip-lines"
        ariaLabel="shell.table.density.label"
        (click)="densityMenu.toggle($event)"
      ></sh-button>
      <sh-menu #densityMenu [items]="densityItems()"></sh-menu>
      }
    </ng-template>
  `,
})
export class ShTableComponent<T = unknown> {
  private readonly _preferences = inject(SH_TABLE_PREFERENCES);

  /**
   * Identifies the table for the density preference. Without it the density
   * menu works but is not remembered.
   */
  readonly tableId = input<string>();
  /**
   * The column config. MUST be a stable reference (a field, never a getter or
   * a method call in the template): a new array on every change detection
   * rebuilds every row, and with PrimeNG inside that freezes the page.
   */
  readonly columns = input.required<ShColumn<T>[]>();
  /** Client mode: all rows. Server mode: the current page. */
  readonly rows = input<T[]>([]);
  readonly dataKey = input('id');
  readonly loading = input(false);
  /** Unset: no paginator, all rows. */
  readonly paging = input<ShTablePaging>();
  readonly sort = model<ShTableSort | undefined>();
  /** 0-based. */
  readonly page = model(0);
  /** Forces a density, overriding (and hiding) the user's choice. */
  readonly density = input<ShTableDensity>();
  /**
   * This table's density until the user picks one (e.g. compact for rows with
   * a second line); the user's choice still wins. Unset: condensed.
   */
  readonly defaultDensity = input<ShTableDensity>();
  readonly skeletonRows = input(5);
  /** Shows the "clear all" link in the empty state; then listen to `clearFilters`. */
  readonly showClearFilters = input(false);

  /** Adds a checkbox column. */
  readonly selectable = input(false);
  /** The selected row objects; persists across pages (identity: `dataKey`). */
  readonly selection = model<T[]>([]);
  /** Shown in a `sh-bulk-bar` while rows are selected. */
  readonly bulkActions = input<ActionMenuItem<T[]>[]>();
  /** Adds a last column with a menu per row. */
  readonly rowActions = input<ActionMenuItem<T>[]>();
  /**
   * Extra CSS classes per row, e.g. the generic tones `row-tone-muted` /
   * `row-tone-danger`. Pass a stable function (a field, not a template
   * literal).
   */
  readonly rowClass = input<(row: T) => string | null | undefined>();
  readonly rowOpenMode = input<RowOpenMode>('none');
  /** The `dataKey` value of the open row (peek mode styling). */
  readonly activeRow = input<unknown>();
  /** A field name or a function returning the group of a row. */
  readonly groupBy = input<string | ((row: T) => string)>();
  /** The group header text; default the key as is. */
  readonly groupLabel = input<(key: string, rows: T[]) => string>();
  /** Query parameter prefix that binds page and sort to the URL; none = no binding. */
  readonly urlParam = input<string>();
  /** Navigation state for URL writes. */
  readonly urlState = input<Record<string, unknown>>();

  readonly queryChange = output<ShTableQuery>();
  readonly rowOpen = output<T>();
  readonly clearFilters = output<void>();

  private readonly _route = inject(ActivatedRoute, { optional: true });
  private readonly _router = inject(Router, { optional: true });

  protected readonly noActions = NO_ACTIONS as ActionMenuItem<T>[];

  private readonly _cells = contentChildren(ShCellDirective);
  protected readonly emptyTemplate = contentChild(ShEmptyDirective, {
    read: TemplateRef,
  });

  protected readonly cellTemplates = computed(() => {
    const map: Record<string, TemplateRef<unknown>> = {};
    for (const cell of this._cells()) map[cell.key()] = cell.template;
    return map;
  });

  protected readonly columnViews = computed<ColumnView[]>(() =>
    this.columns().map((column) => {
      const type = column.type ?? 'text';
      const end =
        (column.align ?? (type === 'number' ? 'end' : 'start')) === 'end';
      const nowrap =
        column.nowrap ??
        (!!column.compact || type === 'date' || type === 'dateTime');
      let sortField: string | undefined;
      if (column.sortable) {
        sortField =
          typeof column.sortable === 'string' ? column.sortable : column.key;
      }
      let defaultFormat: string | undefined;
      if (type === 'date') defaultFormat = 'dd-MM-yyyy';
      if (type === 'dateTime') defaultFormat = 'dd-MM-yyyy HH:mm';
      return {
        column,
        type,
        sortField,
        cellClass: [
          end ? 'cell-end' : '',
          nowrap ? 'cell-nowrap' : '',
          column.truncate ? 'cell-truncate' : '',
          column.compact ? 'cell-compact' : '',
        ]
          .filter(Boolean)
          .join(' '),
        width: column.width ?? (column.compact ? '1%' : null),
        truncate: !!column.truncate,
        format: column.format ?? defaultFormat,
        skeleton: skeletonFor(type),
      };
    })
  );

  protected readonly hasRowActions = computed(
    () => (this.rowActions()?.length ?? 0) > 0
  );

  /** Data columns plus the check and actions columns (the colspan of full-width rows). */
  protected readonly columnCount = computed(
    () =>
      this.columnViews().length +
      (this.selectable() ? 1 : 0) +
      (this.hasRowActions() ? 1 : 0)
  );

  private readonly _selectedKeys = computed(
    () => new Set(this.selection().map((row) => this._keyOf(row)))
  );

  protected readonly skeletonIndexes = computed(() =>
    Array.from({ length: Math.max(0, this.skeletonRows()) }, (_, i) => i)
  );

  // --- paging ---------------------------------------------------------------

  private readonly _pageSizeChoice = signal<number | undefined>(undefined);
  private readonly _configuredPageSize = computed(
    () => this.paging()?.pageSize
  );

  protected readonly pageSize = computed(
    () => this._pageSizeChoice() ?? this._configuredPageSize() ?? 10
  );
  protected readonly pageSizeOptions = computed(
    () => this.paging()?.pageSizeOptions,
    { equal: sameNumbers }
  );
  protected readonly totalRecords = computed(() => {
    const paging = this.paging();
    return paging?.mode === 'server'
      ? paging.total ?? this.rows().length
      : this.rows().length;
  });
  private readonly _lastPage = computed(() =>
    Math.max(0, Math.ceil(this.totalRecords() / this.pageSize()) - 1)
  );
  protected readonly first = computed(
    () => Math.min(this.page(), this._lastPage()) * this.pageSize()
  );

  // --- sorting --------------------------------------------------------------

  protected readonly sortField = computed(() => this.sort()?.field);
  protected readonly sortOrder = computed(() =>
    this.sort()?.order === 'desc' ? -1 : 1
  );

  private readonly _groupKeyOf = computed<((row: T) => string) | undefined>(
    () => {
      const groupBy = this.groupBy();
      if (!groupBy) return undefined;
      if (typeof groupBy === 'function') return groupBy;
      return (row) => String(resolvePath(row, groupBy) ?? '');
    }
  );

  private readonly _sortedRows = computed<T[]>(() => {
    const rows = this.rows();
    const sort = this.sort();
    if (this.paging()?.mode === 'server' || !sort) return rows;
    const view = this.columnViews().find((v) => v.sortField === sort.field);
    if (!view) return rows;
    const column = view.column;
    const valueOf = (row: T) =>
      column.value
        ? column.value(row)
        : (row as Record<string, unknown>)?.[column.key];
    const direction = sort.order === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      // Empty values stay last in both directions.
      if (isEmptyValue(va) || isEmptyValue(vb)) {
        return Number(isEmptyValue(va)) - Number(isEmptyValue(vb));
      }
      return direction * compareValues(va, vb);
    });
  });

  /** Client mode with `groupBy`: the sorted rows ordered by group (stable). */
  private readonly _orderedRows = computed<T[]>(() => {
    const rows = this._sortedRows();
    const keyOf = this._groupKeyOf();
    if (!keyOf || this.paging()?.mode === 'server') return rows;
    const order = new Map<string, number>();
    for (const row of this.rows()) {
      const key = keyOf(row);
      if (!order.has(key)) order.set(key, order.size);
    }
    return [...rows].sort(
      (a, b) => (order.get(keyOf(a)) ?? 0) - (order.get(keyOf(b)) ?? 0)
    );
  });

  protected readonly shownRows = computed<T[]>(() => {
    // While loading, the skeleton rows replace the data (see `loadingbody`).
    if (this.loading()) return EMPTY_ROWS as T[];
    const rows = this._orderedRows();
    if (this.paging()?.mode !== 'client') return rows;
    const first = this.first();
    return rows.slice(first, first + this.pageSize());
  });

  // --- grouping -----------------------------------------------------------

  /**
   * The header row above the first row of each group on the shown page, keyed
   * by that row object. Client mode counts the whole group, server mode the
   * rows of the page.
   */
  protected readonly groupHeaders = computed<Map<unknown, GroupHeader>>(() => {
    const keyOf = this._groupKeyOf();
    if (!keyOf) return EMPTY_HEADERS;
    const shown = this.shownRows();
    if (!shown.length) return EMPTY_HEADERS;
    const pool = this.paging()?.mode === 'client' ? this._orderedRows() : shown;
    const groups = new Map<string, T[]>();
    for (const row of pool) {
      const key = keyOf(row);
      const members = groups.get(key);
      if (members) members.push(row);
      else groups.set(key, [row]);
    }
    const labelOf = this.groupLabel();
    const headers = new Map<unknown, GroupHeader>();
    let previous: string | undefined;
    shown.forEach((row, index) => {
      const key = keyOf(row);
      if (index === 0 || key !== previous) {
        const members = groups.get(key) ?? [row];
        headers.set(row, {
          key,
          label: labelOf ? labelOf(key, members) : key,
          count: members.length,
        });
      }
      previous = key;
    });
    return headers;
  });

  // --- density --------------------------------------------------------------

  private readonly _densityChoice = signal<ShTableDensity | undefined>(
    undefined
  );

  protected readonly showDensityControl = computed(
    () => this.density() === undefined
  );

  protected readonly effectiveDensity = computed<ShTableDensity>(() => {
    const forced = this.density();
    if (forced) return forced;
    const choice = this._densityChoice();
    if (choice) return choice;
    const id = this.tableId();
    return (
      (id ? this._preferences.density(id) : undefined) ??
      this.defaultDensity() ??
      SH_TABLE_DEFAULT_DENSITY
    );
  });

  protected readonly densityItems = computed<ShMenuItem[]>(() => {
    const current = this.effectiveDensity();
    return SH_TABLE_DENSITIES.map((density) => ({
      label: `shell.table.density.${density}`,
      icon: density === current ? 'pi pi-check' : 'pi pi-fw',
      command: () => this._chooseDensity(density),
    }));
  });

  // --- URL state ------------------------------------------------------------

  // The query params of the route; `null` until the first emission.
  private readonly _urlParams = signal<ParamMap | null>(null);
  private _urlInitialised = false;
  // The sort on init: what an absent `-sort` param stands for afterwards.
  private _defaultSort: ShTableSort | undefined;

  constructor() {
    // A new configured page size drops the user's own choice.
    effect(
      () => {
        this._configuredPageSize();
        untracked(() => this._pageSizeChoice.set(undefined));
      },
      { allowSignalWrites: true }
    );

    // URL -> table: follow the route's query params.
    effect(
      (onCleanup) => {
        if (!this.urlParam() || !this._route) return;
        const subscription = this._route.queryParamMap.subscribe((params) =>
          this._urlParams.set(params)
        );
        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const param = this.urlParam();
        const params = this._urlParams();
        const columns = this.columnViews();
        if (!param || !params) return;
        untracked(() => this._applyUrl(param, params, columns));
      },
      { allowSignalWrites: true }
    );
  }

  protected isSelected(row: T): boolean {
    return this._selectedKeys().has(this._keyOf(row));
  }

  protected rowClassOf(row: T): string {
    return this.rowClass()?.(row) ?? '';
  }

  protected isActive(row: T): boolean {
    const active = this.activeRow();
    return active !== undefined && this._keyOf(row) === active;
  }

  protected onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.pageSize();
    const first = event.first ?? 0;

    const field =
      typeof event.sortField === 'string' ? event.sortField : undefined;
    const sort: ShTableSort | undefined = field
      ? { field, order: event.sortOrder === -1 ? 'desc' : 'asc' }
      : undefined;
    const sortChanged = !sameSort(sort, this.sort());
    // A new sort starts at page 1 (`resetPageOnSort` is off: PrimeNG would
    // also reset the page when the sort is set from outside, e.g. by the URL).
    const page = sortChanged ? 0 : Math.floor(first / rows);

    // PrimeNG also fires this when `sort` or `first` are set from outside; that
    // is no user change, so nothing is emitted for it.
    if (!sortChanged && rows === this.pageSize() && page === this.page()) {
      return;
    }

    if (rows !== this.pageSize()) this._pageSizeChoice.set(rows);
    if (sortChanged) this.sort.set(sort);
    if (page !== this.page()) this.page.set(page);

    this.queryChange.emit({ page, pageSize: rows, sort });
    this._writeUrl(page, sort);
  }

  private _keyOf(row: T): unknown {
    return resolvePath(row, this.dataKey());
  }

  private _applyUrl(
    param: string,
    params: ParamMap,
    columns: ColumnView[]
  ): void {
    const initial = !this._urlInitialised;
    if (initial) {
      this._urlInitialised = true;
      this._defaultSort = this.sort();
    }

    // Invalid values count as absent. Absent: on init the model's own value
    // stands; later (back/forward) the table returns to page 1 / its default sort.
    const rawPage = params.get(`${param}-page`);
    const urlPage =
      rawPage !== null && /^[1-9]\d*$/.test(rawPage)
        ? Number(rawPage) - 1
        : undefined;
    const urlSort = this._parseSort(params.get(`${param}-sort`), columns);

    const page = urlPage ?? (initial ? this.page() : 0);
    const sort = urlSort ?? (initial ? this.sort() : this._defaultSort);

    const pageChanged = page !== this.page();
    const sortChanged = !sameSort(sort, this.sort());
    if (!pageChanged && !sortChanged) return;
    if (sortChanged) this.sort.set(sort);
    if (pageChanged) this.page.set(page);
    this.queryChange.emit({ page, pageSize: this.pageSize(), sort });
  }

  private _parseSort(
    raw: string | null,
    columns: ColumnView[]
  ): ShTableSort | undefined {
    if (!raw) return undefined;
    const dot = raw.lastIndexOf('.');
    if (dot < 1) return undefined;
    const field = raw.slice(0, dot);
    const order = raw.slice(dot + 1);
    if (order !== 'asc' && order !== 'desc') return undefined;
    if (!columns.some((view) => view.sortField === field)) return undefined;
    return { field, order };
  }

  private _writeUrl(page: number, sort: ShTableSort | undefined): void {
    const param = this.urlParam();
    if (!param || !this._router || !this._route) return;
    const pageKey = `${param}-page`;
    const sortKey = `${param}-sort`;
    const pageValue = page > 0 ? String(page + 1) : null;
    const sortValue =
      sort && !sameSort(sort, this._defaultSort)
        ? `${sort.field}.${sort.order}`
        : null;
    const params = this._urlParams();
    if (
      (params?.get(pageKey) ?? null) === pageValue &&
      (params?.get(sortKey) ?? null) === sortValue
    ) {
      return;
    }
    void this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { [pageKey]: pageValue, [sortKey]: sortValue },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      state: this.urlState(),
    });
  }

  private _chooseDensity(density: ShTableDensity): void {
    this._densityChoice.set(density);
    const id = this.tableId();
    if (id) this._preferences.setDensity(id, density);
  }
}
